import {
    Container,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Page from '../components/Page';
import ListPageTitle from 'src/components/misc/ListPageTitle';
import Dialog from 'src/components/misc/alerts/Dialog';
import ServerError from 'src/components/misc/alerts/ServerError';
import CenterLoading from 'src/components/misc/CenterLoading';
import killTransferService from 'src/services/KillTransferServiceClass';

const factionColor = (familyName) =>
    familyName && familyName.toLowerCase().includes('light') ? 'primary' : 'secondary';

export default () => {
    const title = 'Kills Migration';

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [seasonsCharacters, setSeasonsCharacters] = useState([]);
    const [shaiyaCharacters, setShaiyaCharacters] = useState([]);

    const [selectedCharId, setSelectedCharId] = useState('');
    const [selectedSource, setSelectedSource] = useState(null);
    const [openDia, setOpenDia] = useState(false);
    const [transferring, setTransferring] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const loadData = () => {
        setLoading(true);
        setLoadError(null);
        killTransferService
            .getCharacters()
            .then((data) => {
                setSeasonsCharacters(data?.seasonsCharacters ?? []);
                setShaiyaCharacters(data?.shaiyaCharacters ?? []);
            })
            .catch((err) => {
                console.error(err);
                setLoadError(
                    err?.response?.data?.msg ||
                        'Could not load your characters. Please try again later.',
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(loadData, []);

    const handleTransferClick = (sourceChar) => {
        if (!selectedCharId) {
            setError('Please select a target character first.');
            return;
        }
        setError(null);
        setSelectedSource(sourceChar);
        setOpenDia(true);
    };

    const handleConfirm = () => {
        setTransferring(true);
        setError(null);
        killTransferService
            .transferKills({
                fromCharId: selectedSource.charId,
                toCharId: selectedCharId,
            })
            .then(() => {
                const target = shaiyaCharacters.find((c) => c.charId === selectedCharId);
                setSuccess(
                    `Successfully transferred ${selectedSource.totalKills} kills from "${selectedSource.charName}" to "${target?.charName}".`,
                );
                setOpenDia(false);
                setSelectedSource(null);
                loadData();
            })
            .catch((err) => {
                console.error(err);
                setError(
                    err?.response?.data?.msg ||
                        'Transfer failed. Please try again or contact support.',
                );
            })
            .finally(() => {
                setTransferring(false);
            });
    };

    const handleClose = () => {
        if (transferring) return;
        setOpenDia(false);
        setSelectedSource(null);
    };

    const targetCharacter = shaiyaCharacters.find((c) => c.charId === selectedCharId);

    return (
        <Page title={title}>
            <Container>
                <ListPageTitle mb={5}>{title}</ListPageTitle>

                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottomWidth: 0.1, borderBottom: 1, pb: 3, mb: 4 }}>
                        <Typography sx={{ mb: 2 }}>
                            Transfer your Season kills to a character on the normal server. Select
                            the target character below, then choose which Season character's kills
                            to transfer. The kills will be added to those already attributed to
                            that character.
                        </Typography>
                        <ServerError open={true} sx={{ width: '100%' }}>
                            Kills can only be transferred once per Season character. This action
                            cannot be undone.
                        </ServerError>
                    </Box>

                    {loading ? (
                        <CenterLoading />
                    ) : loadError ? (
                        <Alert severity="error">{loadError}</Alert>
                    ) : (
                        <>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Step 1: Select Target Character (Normal Server)
                            </Typography>
                            <FormControl sx={{ minWidth: 300, mb: 4 }} fullWidth>
                                <InputLabel id="character-select-label">
                                    Select Character
                                </InputLabel>
                                <Select
                                    labelId="character-select-label"
                                    id="character-select"
                                    value={selectedCharId}
                                    onChange={(e) => {
                                        setSelectedCharId(e.target.value);
                                        setError(null);
                                    }}
                                    label="Select Character"
                                    disabled={shaiyaCharacters.length === 0}
                                >
                                    {shaiyaCharacters.map((char) => (
                                        <MenuItem key={char.charId} value={char.charId}>
                                            {char.charName} — Lv.{char.level} {char.jobName} (
                                            {char.familyName})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {shaiyaCharacters.length === 0 && (
                                <Alert severity="warning" sx={{ mb: 3 }}>
                                    No characters found on the normal server.
                                </Alert>
                            )}

                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Step 2: Transfer Season Kills
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {success}
                                </Alert>
                            )}

                            {seasonsCharacters.length === 0 ? (
                                <Alert severity="info">
                                    You have no Season characters with kills available to transfer.
                                </Alert>
                            ) : (
                                <TableContainer component={Paper} sx={{ mb: 4 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Character</TableCell>
                                                <TableCell>Class</TableCell>
                                                <TableCell>Faction</TableCell>
                                                <TableCell align="right">Level</TableCell>
                                                <TableCell align="right">Total Kills</TableCell>
                                                <TableCell align="center">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {seasonsCharacters.map((entry) => (
                                                <TableRow
                                                    key={entry.charId}
                                                    sx={{
                                                        opacity: entry.alreadyTransferred
                                                            ? 0.55
                                                            : 1,
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="subtitle2">
                                                            {entry.charName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{entry.jobName}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={entry.familyName}
                                                            size="small"
                                                            color={factionColor(entry.familyName)}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {entry.level}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{ color: 'success.main' }}
                                                        >
                                                            {entry.alreadyTransferred
                                                                ? '—'
                                                                : entry.totalKills}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {entry.alreadyTransferred ? (
                                                            <Chip
                                                                label="Transferred"
                                                                color="success"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ) : (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() =>
                                                                    handleTransferClick(entry)
                                                                }
                                                                disabled={!selectedCharId}
                                                            >
                                                                Transfer
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}

                    <Dialog
                        buttonText={'Cancel'}
                        openDialog={!!openDia}
                        handleButton={handleClose}
                        handleButton2={handleConfirm}
                        buttonText2={transferring ? 'Transferring...' : 'Confirm Transfer'}
                        style={{ color: 'white' }}
                        disabledButton1={transferring}
                        disabledButton2={transferring}
                        noIcon
                    >
                        {transferring ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {`You are about to transfer `}
                                <span style={{ fontWeight: 'bold' }}>
                                    {selectedSource?.totalKills} kills
                                </span>
                                {` from Season character `}
                                <span style={{ fontWeight: 'bold' }}>
                                    "{selectedSource?.charName}"
                                </span>
                                {` to `}
                                <span style={{ fontWeight: 'bold' }}>
                                    "{targetCharacter?.charName}"
                                </span>
                                {`. These kills will be added to the ${
                                    targetCharacter?.totalKills ?? 0
                                } already attributed to this character. This action cannot be undone.`}
                            </>
                        )}
                    </Dialog>
                </Box>
            </Container>
        </Page>
    );
};
